import React, { Suspense, useMemo } from 'react'
import { graphql } from 'babel-plugin-relay/macro'
import { useSuspenseFragment } from 'react-relay-offline'
import { csx } from '@vtex/admin-ui'

import { ProductSkuAvailabilityDelivery } from './ProductSkuAvailabilityDelivery'
import { ProductSkuAvailabilityPickup } from './ProductSkuAvailabilityPickup'
import { ProductSkuAvailabilityTakeAway } from './ProductSkuAvailabilityTakeAway'
import { useProductPageUrlParams } from '../../hooks'
import type { ProductSkuAvailability_Product$key } from './__generated__/ProductSkuAvailability_Product.graphql'
import { ProductSkuAvailabilityLoading } from './ProductSkuAvailabilityLoading'
import { ProductSkuAvailabilityNotAvailable } from './ProductSkuAvailabilityNotAvailable'

const productFragment = graphql`
  fragment ProductSkuAvailability_Product on Product {
    skus {
      edges {
        node {
          id
          offers(addresses: $addresses) {
            edges {
              __typename
            }
            ...ProductSkuAvailabilityDelivery_ProductSkuOfferConnection
            ...ProductSkuAvailabilityPickup_ProductSkuOfferConnection
            ...ProductSkuAvailabilityTakeAway_ProductSkuOfferConnection
          }
        }
      }
    }
  }
`

type ProductSkuAvailabilityContentProps = {
  productRef: ProductSkuAvailability_Product$key
}

const ProductSkuAvailabilityContent = ({
  productRef,
}: ProductSkuAvailabilityContentProps) => {
  const { selectedSkuId } = useProductPageUrlParams()
  const product = useSuspenseFragment(productFragment, productRef)

  const selectedSku = useMemo(() => {
    return product.skus.edges.find(
      (skuEdge) => skuEdge.node.id === selectedSkuId
    )
  }, [product.skus.edges, selectedSkuId])

  if (!selectedSku?.node.offers) {
    return null
  }

  const availabilityProps = {
    productSkuOfferConnectionRef: selectedSku.node.offers,
  }

  const hasOffer = !!selectedSku.node.offers.edges.length

  return (
    <>
      {hasOffer ? (
        <div className={csx({ paddingX: '$space-5' })}>
          <ProductSkuAvailabilityDelivery {...availabilityProps} />
          <ProductSkuAvailabilityPickup {...availabilityProps} />
          <ProductSkuAvailabilityTakeAway {...availabilityProps} />
        </div>
      ) : (
        <ProductSkuAvailabilityNotAvailable />
      )}
    </>
  )
}

type ProductSkuAvailabilityProps = {
  productRef: ProductSkuAvailability_Product$key
}

export const ProductSkuAvailability = (props: ProductSkuAvailabilityProps) => {
  return (
    <Suspense fallback={<ProductSkuAvailabilityLoading />}>
      <ProductSkuAvailabilityContent {...props} />
    </Suspense>
  )
}
